import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Trash2, Pencil, Upload, FolderPlus, ChevronDown, ChevronUp,
  Loader2, X, FileText, Save, AlertCircle, CheckCircle2, BookOpen,
  ArrowUpDown, GripVertical, RefreshCw, Stethoscope, Layers, Eye, EyeOff, Star,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useReorder } from '../../lib/useReorder';

function formatSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function AdminPeriodicalsPage() {
  const [data, setData] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null); // null = all tabs
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [modal, setModal] = useState(null); // {type, payload}

  // Sections belonging to the active tab (or all when no filter).
  const filteredData = useMemo(() => {
    if (!activeGroup) return data;
    return data.filter((s) => (s.group || 'flash') === activeGroup);
  }, [data, activeGroup]);

  // Top-level reorder (sections).
  const sectionDnd = useReorder(filteredData, (s) => s.id);

  async function refresh() {
    setLoading(true);
    try {
      const [secRes, grpRes] = await Promise.all([
        api.get('/periodicals'),
        api.get('/admin/periodicals/groups'),
      ]);
      setData(secRes.data || []);
      setGroups(grpRes.data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3500);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  // Sections visible (reordered when in DnD mode).
  const visibleSections = useMemo(() => {
    if (!sectionDnd.reorderMode) return filteredData;
    const byId = Object.fromEntries(filteredData.map((s) => [s.id, s]));
    return sectionDnd.orderDraft.map((id) => byId[id]).filter(Boolean);
  }, [filteredData, sectionDnd.reorderMode, sectionDnd.orderDraft]);

  async function saveSectionOrder() {
    await sectionDnd.saveOrder(async (ids) => {
      await api.put('/admin/periodicals/sections/reorder', { ids });
      showOk('Orden de secciones actualizado');
      await refresh();
    });
  }

  function toggleSection(id) {
    if (sectionDnd.reorderMode) return; // ignore expand when reordering
    setExpanded((prev) => ({ ...prev, [id]: !(prev[id] !== false) ? true : false }));
  }
  function isOpen(id) { return expanded[id] !== false; }

  function showOk(msg) { setFeedback({ type: 'success', message: msg }); }
  function showErr(msg) { setFeedback({ type: 'error', message: msg }); }

  async function deleteSection(s) {
    if (!window.confirm(`¿Eliminar "${s.name.es}" y TODOS sus periodos/archivos?`)) return;
    try {
      await api.delete(`/admin/periodicals/sections/${s.id}`);
      showOk(`Sección "${s.name.es}" eliminada`);
      refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    }
  }
  async function deletePeriod(p) {
    if (!window.confirm(`¿Eliminar "${p.label.es}" y sus archivos?`)) return;
    try {
      await api.delete(`/admin/periodicals/periods/${p.id}`);
      showOk(`Periodo "${p.label.es}" eliminado`);
      refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    }
  }
  async function deleteFile(f) {
    if (!window.confirm(`¿Eliminar archivo "${f.title}"?`)) return;
    try {
      await api.delete(`/admin/periodicals/files/${f.id}`);
      showOk(`Archivo "${f.title}" eliminado`);
      refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    }
  }

  async function toggleFeatured(section) {
    try {
      await api.put(`/admin/periodicals/sections/${section.id}/featured`, { featured: !section.featured });
      showOk(section.featured ? `"${section.name.es}" ya no está destacada` : `"${section.name.es}" destacada (aparecerá arriba)`);
      refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    }
  }

  async function persistPeriodOrder(sectionId, ids) {
    await api.put('/admin/periodicals/periods/reorder', { ids });
    showOk('Orden de periodos actualizado');
    await refresh();
  }

  return (
    <div data-testid="admin-periodicals-page">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <BookOpen size={22} className="text-cmic-600" />
            Periodicals
          </h1>
          <p className="text-charcoal-500 mt-1 text-sm">Crea secciones (Ej: <em>Flash 2026</em>), agrega periodos y sube PDFs. La búsqueda se reindexa automáticamente.</p>
        </div>
        <div className="flex items-center gap-2">
          {!sectionDnd.reorderMode ? (
            <>
              <button
                onClick={async () => {
                  setFeedback(null);
                  try {
                    const { data: res } = await api.post('/admin/periodicals/reindex');
                    showOk(`Búsqueda reindexada · ${res.indexed_files} archivos`);
                  } catch (e) {
                    setFeedback({ kind: 'error', text: formatApiError(e) });
                  }
                }}
                data-testid="admin-periodicals-reindex-btn"
                title="Reconstruye el índice de búsqueda con todos los PDFs en disco"
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                <RefreshCw size={14} /> Reindexar búsqueda
              </button>
              <button
                onClick={() => setModal({ type: 'diagnostics' })}
                data-testid="admin-periodicals-diagnostics-btn"
                title="Ver qué PDFs son buscables (con texto extraíble)"
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                <Stethoscope size={14} /> Diagnóstico
              </button>
              <button
                onClick={() => setModal({ type: 'groups-manage' })}
                data-testid="admin-periodicals-manage-groups-btn"
                title="Crear, renombrar, ocultar o reordenar las pestañas públicas"
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                <Layers size={14} /> Administrar pestañas
              </button>
              <button
                onClick={sectionDnd.enterReorder}
                disabled={filteredData.length < 2}
                data-testid="admin-periodicals-reorder-sections-btn"
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowUpDown size={14} /> Reordenar secciones
              </button>
              <button
                onClick={() => setModal({ type: 'section-new', group: activeGroup || (groups[0] && groups[0].key) || 'flash' })}
                data-testid="admin-periodicals-new-section-btn"
                className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm"
              >
                <Plus size={16} /> Nueva sección
              </button>
            </>
          ) : (
            <>
              <button onClick={sectionDnd.cancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                <X size={14} /> Cancelar
              </button>
              <button
                onClick={saveSectionOrder}
                disabled={sectionDnd.saving}
                data-testid="admin-periodicals-save-sections-order-btn"
                className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-600 disabled:opacity-60"
              >
                {sectionDnd.saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar orden
              </button>
            </>
          )}
        </div>
      </div>

      {feedback && (
        <div data-testid="admin-periodicals-feedback"
          className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-6 text-sm font-medium ${
            feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
          {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {feedback.message}
        </div>
      )}

      {sectionDnd.reorderMode && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-4 py-2.5 mb-4 flex items-center gap-2" data-testid="admin-periodicals-reorder-hint">
          <GripVertical size={14} /> Arrastra las secciones para reordenarlas. Haz clic en "Guardar orden" cuando termines.
        </div>
      )}

      {!sectionDnd.reorderMode && groups.length > 0 && (
        <div className="flex items-center gap-1.5 mb-6 border-b border-gray-200 overflow-x-auto no-scrollbar" data-testid="admin-periodicals-group-tabs">
          <button
            onClick={() => setActiveGroup(null)}
            data-testid="admin-periodicals-group-tab-all"
            className={`px-4 py-2.5 text-sm font-semibold transition-colors -mb-px border-b-2 whitespace-nowrap flex-shrink-0 ${!activeGroup ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-700'}`}
          >
            Todas
          </button>
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => setActiveGroup(g.key)}
              data-testid={`admin-periodicals-group-tab-${g.key}`}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors -mb-px border-b-2 whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${activeGroup === g.key ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-700'}`}
            >
              {g.name_es}
              {g.hidden && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full"><EyeOff size={10} /> Oculta</span>}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>
      ) : filteredData.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <BookOpen size={36} className="text-charcoal-300 mx-auto mb-2" />
          <p className="text-charcoal-500 text-sm mb-4">
            {activeGroup ? 'Esta pestaña aún no tiene secciones.' : 'Aún no hay secciones. Crea la primera (ej. "Flash 2026").'}
          </p>
          <button onClick={() => setModal({ type: 'section-new', group: activeGroup || (groups[0] && groups[0].key) || 'flash' })} className="inline-flex items-center gap-2 bg-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700">
            <Plus size={14} /> Nueva sección
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleSections.map((section) => {
            const open = isOpen(section.id);
            const totalFiles = section.periods.reduce((acc, p) => acc + p.files.length, 0);
            const inSectionReorder = sectionDnd.reorderMode;
            return (
              <div
                key={section.id}
                data-testid={`admin-section-${section.id}`}
                draggable={inSectionReorder}
                {...(inSectionReorder ? sectionDnd.dragProps(section.id) : {})}
                className={`bg-white rounded-xl border overflow-hidden ${inSectionReorder ? 'border-amber-300 cursor-move ring-1 ring-amber-200/40' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between px-5 py-4 bg-charcoal-800 text-white">
                  <button onClick={() => toggleSection(section.id)}
                    data-testid={`admin-section-toggle-${section.id}`}
                    className="flex items-center gap-3 flex-1 text-left">
                    {inSectionReorder ? (
                      <GripVertical size={18} className="text-amber-300" />
                    ) : (
                      open ? <ChevronUp size={18} className="text-charcoal-300" /> : <ChevronDown size={18} className="text-charcoal-300" />
                    )}
                    <span className="font-bold text-base uppercase tracking-wide">{section.name.es}</span>
                    <span className="text-xs text-charcoal-400 hidden sm:inline">· {section.periods.length} periodo(s) · {totalFiles} PDF(s)</span>
                  </button>
                  {!inSectionReorder && (
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setModal({ type: 'section-file-new', section })}
                        data-testid={`admin-section-${section.id}-upload`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-cmic-500/90 hover:bg-cmic-500 text-white px-3 py-1.5 rounded-lg">
                        <Upload size={14} /> Subir PDF
                      </button>
                      <button onClick={() => setModal({ type: 'period-new', section })}
                        data-testid={`admin-section-${section.id}-add-period`}
                        className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg">
                        <FolderPlus size={14} /> Agregar periodo
                      </button>
                      <button onClick={() => toggleFeatured(section)}
                        data-testid={`admin-section-${section.id}-feature`}
                        className={`p-2 rounded-lg transition-colors ${section.featured ? 'text-amber-300 hover:bg-white/10' : 'text-charcoal-300 hover:bg-white/10 hover:text-white'}`}
                        title={section.featured ? 'Quitar destacado' : 'Destacar (fijar arriba)'}>
                        <Star size={14} className={section.featured ? 'fill-amber-300' : ''} />
                      </button>
                      <button onClick={() => setModal({ type: 'section-edit', section })}
                        data-testid={`admin-section-${section.id}-edit`}
                        className="p-2 rounded-lg text-charcoal-300 hover:bg-white/10 hover:text-white" title="Editar sección">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => deleteSection(section)}
                        data-testid={`admin-section-${section.id}-delete`}
                        className="p-2 rounded-lg text-charcoal-300 hover:bg-red-500/30 hover:text-white" title="Eliminar sección">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {open && !inSectionReorder && (
                  <PeriodsList
                    section={section}
                    onAddPeriod={() => setModal({ type: 'period-new', section })}
                    onEditPeriod={(period) => setModal({ type: 'period-edit', period, section })}
                    onDeletePeriod={deletePeriod}
                    onUploadFile={(period) => setModal({ type: 'file-new', period, section })}
                    onDeleteFile={deleteFile}
                    persistOrder={(ids) => persistPeriodOrder(section.id, ids)}
                    showErr={showErr}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal && modal.type === 'diagnostics' && (
        <DiagnosticsModal onClose={() => setModal(null)} />
      )}

      {modal && modal.type === 'groups-manage' && (
        <GroupsModal
          groups={groups}
          onClose={() => setModal(null)}
          onChanged={refresh}
          showOk={showOk}
          showErr={showErr}
        />
      )}

      {modal && modal.type !== 'diagnostics' && modal.type !== 'groups-manage' && (
        <PeriodicalsModal
          modal={modal}
          groups={groups}
          onClose={() => setModal(null)}
          onRefresh={refresh}
          onSuccess={(msg) => { showOk(msg); refresh(); setModal(null); }}
          onError={(msg) => showErr(msg)}
        />
      )}
    </div>
  );
}

// Per-section periods list — has its own (independent) reorder mode for the
// periods inside this section. Uses the same useReorder hook + the global
// PUT /admin/periodicals/periods/reorder endpoint (it reorders by passing an
// array of ids; only periods belonging to this section are passed).
function PeriodsList({ section, onAddPeriod, onEditPeriod, onDeletePeriod, onUploadFile, onDeleteFile, persistOrder, showErr }) {
  const dnd = useReorder(section.periods, (p) => p.id);

  const visible = useMemo(() => {
    if (!dnd.reorderMode) return section.periods;
    const byId = Object.fromEntries(section.periods.map((p) => [p.id, p]));
    return dnd.orderDraft.map((id) => byId[id]).filter(Boolean);
  }, [section.periods, dnd.reorderMode, dnd.orderDraft]);

  async function saveOrder() {
    try {
      await dnd.saveOrder(persistOrder);
    } catch (err) {
      showErr('No se pudo guardar el orden');
    }
  }

  if (section.periods.length === 0) {
    return (
      <div className="px-5 py-6 text-center">
        <p className="text-sm text-charcoal-400 mb-3">Esta sección no tiene periodos aún.</p>
        <button onClick={onAddPeriod}
          data-testid={`admin-section-${section.id}-add-period-empty`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-cmic-600 hover:text-cmic-700">
          <FolderPlus size={14} /> Agregar primer periodo
        </button>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      <div className="flex items-center justify-end gap-2 px-5 py-2 bg-gray-50/50">
        {!dnd.reorderMode ? (
          <button
            onClick={dnd.enterReorder}
            disabled={section.periods.length < 2}
            data-testid={`admin-section-${section.id}-reorder-periods-btn`}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-charcoal-600 hover:text-cmic-600 disabled:opacity-40"
          >
            <ArrowUpDown size={12} /> Reordenar periodos
          </button>
        ) : (
          <>
            <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-charcoal-600 hover:text-charcoal-800">
              <X size={12} /> Cancelar
            </button>
            <button
              onClick={saveOrder}
              disabled={dnd.saving}
              data-testid={`admin-section-${section.id}-save-periods-order-btn`}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-60"
            >
              {dnd.saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar orden
            </button>
          </>
        )}
      </div>

      {visible.map((period) => (
        <div
          key={period.id}
          data-testid={`admin-period-${period.id}`}
          draggable={dnd.reorderMode}
          {...(dnd.reorderMode ? dnd.dragProps(period.id) : {})}
          className={dnd.reorderMode ? 'cursor-move bg-amber-50/30 hover:bg-amber-50/60' : ''}
        >
          <div className="flex items-center justify-between px-5 py-3 bg-gray-50/60">
            <div className="flex items-center gap-3">
              {dnd.reorderMode && <GripVertical size={14} className="text-amber-500" />}
              <span className="text-sm font-semibold text-charcoal-700">{period.label.es}</span>
              <span className="text-xs text-charcoal-400">{period.files.length} archivo(s)</span>
            </div>
            {!dnd.reorderMode && (
              <div className="flex items-center gap-1.5">
                <button onClick={() => onUploadFile(period)}
                  data-testid={`admin-period-${period.id}-upload`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium bg-cmic-50 hover:bg-cmic-100 text-cmic-700 px-3 py-1.5 rounded-lg">
                  <Upload size={13} /> Subir PDF
                </button>
                <button onClick={() => onEditPeriod(period)}
                  className="p-1.5 rounded-lg text-charcoal-400 hover:bg-gray-100 hover:text-charcoal-700" title="Editar">
                  <Pencil size={13} />
                </button>
                <button onClick={() => onDeletePeriod(period)}
                  data-testid={`admin-period-${period.id}-delete`}
                  className="p-1.5 rounded-lg text-charcoal-400 hover:bg-red-50 hover:text-red-600" title="Eliminar">
                  <Trash2 size={13} />
                </button>
              </div>
            )}
          </div>
          {period.files.length > 0 && !dnd.reorderMode && (
            <div className="bg-white">
              {period.files.map((f) => (
                <div key={f.id} data-testid={`admin-file-${f.id}`} className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-gray-50 last:border-b-0">
                  <a href={f.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 min-w-0 flex-1 group">
                    <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                      <FileText size={16} className="text-red-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal-700 truncate group-hover:text-cmic-600">{f.title}</p>
                      <p className="text-xs text-charcoal-400">{formatSize(f.size_bytes)}</p>
                    </div>
                  </a>
                  <button onClick={() => onDeleteFile(f)}
                    data-testid={`admin-file-${f.id}-delete`}
                    className="p-1.5 rounded-lg text-charcoal-400 hover:bg-red-50 hover:text-red-600" title="Eliminar archivo">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PeriodicalsModal({ modal, groups = [], onClose, onSuccess, onError, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(() => initForm(modal));
  const [fileObjs, setFileObjs] = useState([]);
  const [statuses, setStatuses] = useState([]); // per-file: {state:'queued'|'uploading'|'done'|'error', warned?, msg?}
  const [doneSummary, setDoneSummary] = useState(null); // {ok, warned, failed}
  // Section-level upload: which period the dropped files land in.
  const sectionPeriods = modal.section?.periods || [];
  const [targetPeriodId, setTargetPeriodId] = useState(() => sectionPeriods[0]?.id || '');
  const [folderMode, setFolderMode] = useState(() => (modal.section?.periods || []).length === 0 && modal.type === 'section-file-new');
  const inputFileRef = useRef(null);
  const inputFolderRef = useRef(null);

  const isFileUpload = modal.type === 'file-new' || modal.type === 'section-file-new';
  const uploadPeriodId = modal.type === 'section-file-new' ? targetPeriodId : modal.period?.id;

  // Group folder-picked files by their immediate parent folder → each becomes a period.
  function groupByFolder(files) {
    const groups = {};
    for (const f of files) {
      const rel = f.webkitRelativePath || f.name;
      const parts = rel.split('/');
      const label = parts.length >= 2 ? parts[parts.length - 2] : (modal.section?.name?.es || 'General');
      (groups[label] = groups[label] || []).push(f);
    }
    return groups;
  }
  const folderGroups = folderMode ? groupByFolder(fileObjs) : {};

  // Folder upload: each subfolder = a period (created if missing), files uploaded into it.
  async function bulkUploadFolder() {
    if (!fileObjs.length) { setError('La carpeta no contiene PDFs.'); return; }
    setSaving(true); setError(''); setDoneSummary(null);
    setStatuses(fileObjs.map(() => ({ state: 'queued' })));
    const idxMap = new Map(); fileObjs.forEach((f, i) => idxMap.set(f, i));
    const existing = {};
    sectionPeriods.forEach((p) => { existing[(p.label?.es || '').trim()] = p.id; });
    let nextSort = (sectionPeriods.length ? Math.max(...sectionPeriods.map((p) => p.sort_order || 0)) : 0) + 1;
    let ok = 0; let warned = 0; let failed = 0;
    const groups = groupByFolder(fileObjs);
    const labels = Object.keys(groups).sort();
    for (const label of labels) {
      let pid = existing[label.trim()];
      if (!pid) {
        try {
          const { data } = await api.post('/admin/periodicals/periods', { label_es: label, label_en: label, sort_order: nextSort++, section_id: modal.section.id });
          pid = data.id; existing[label.trim()] = pid;
        } catch (e) {
          groups[label].forEach((f) => { const gi = idxMap.get(f); failed++; setStatuses((prev) => prev.map((s, j) => (j === gi ? { state: 'error', msg: 'No se pudo crear el periodo' } : s))); });
          continue;
        }
      }
      const files = groups[label];
      let cursor = 0;
      const worker = async () => {
        while (cursor < files.length) {
          const f = files[cursor]; cursor += 1; const gi = idxMap.get(f);
          setStatuses((prev) => prev.map((s, j) => (j === gi ? { state: 'uploading' } : s)));
          try {
            const fd = new FormData();
            fd.append('period_id', pid);
            fd.append('title', f.name.replace(/\.pdf$/i, ''));
            fd.append('file', f);
            const { data: res } = await api.post('/admin/periodicals/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const w = !!(res && res.searchable === false); if (w) warned++;
            ok++;
            setStatuses((prev) => prev.map((s, j) => (j === gi ? { state: 'done', warned: w } : s)));
          } catch (err) {
            failed++;
            setStatuses((prev) => prev.map((s, j) => (j === gi ? { state: 'error', msg: formatApiError(err.response?.data?.detail) || 'Error' } : s)));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, files.length) }, worker));
    }
    setDoneSummary({ ok, warned, failed, periods: labels.length });
    onRefresh && onRefresh();
    setSaving(false);
  }

  // Concurrency-limited bulk uploader so migrating ~50 PDFs is fast and resilient.
  async function bulkUpload(retryFailedOnly = false) {
    const indices = retryFailedOnly
      ? fileObjs.map((_, i) => i).filter((i) => statuses[i]?.state === 'error')
      : fileObjs.map((_, i) => i);
    if (!indices.length) return;
    setSaving(true); setError(''); setDoneSummary(null);
    setStatuses((prev) => {
      const base = retryFailedOnly ? [...prev] : fileObjs.map(() => ({ state: 'queued' }));
      indices.forEach((i) => { base[i] = { state: 'queued' }; });
      return base;
    });
    let ok = 0; let warned = 0; let failed = 0;
    let cursor = 0;
    const worker = async () => {
      while (cursor < indices.length) {
        const i = indices[cursor]; cursor += 1;
        const f = fileObjs[i];
        setStatuses((prev) => prev.map((s, idx) => (idx === i ? { state: 'uploading' } : s)));
        try {
          const fd = new FormData();
          fd.append('period_id', uploadPeriodId);
          const derived = f.name.replace(/\.pdf$/i, '');
          fd.append('title', (fileObjs.length === 1 && form.title.trim()) ? form.title.trim() : derived);
          fd.append('file', f);
          const { data: res } = await api.post('/admin/periodicals/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const w = !!(res && res.searchable === false);
          if (w) warned += 1;
          ok += 1;
          setStatuses((prev) => prev.map((s, idx) => (idx === i ? { state: 'done', warned: w } : s)));
        } catch (err) {
          failed += 1;
          setStatuses((prev) => prev.map((s, idx) => (idx === i ? { state: 'error', msg: formatApiError(err.response?.data?.detail) || 'Error al subir' } : s)));
        }
      }
    };
    const concurrency = Math.min(3, indices.length);
    await Promise.all(Array.from({ length: concurrency }, worker));
    setDoneSummary({ ok, warned, failed });
    onRefresh && onRefresh();
    setSaving(false);
  }

  async function submit(e) {
    e?.preventDefault?.();
    if (isFileUpload) {
      if (!fileObjs.length) { setError('Selecciona uno o más archivos PDF'); return; }
      if (folderMode) { await bulkUploadFolder(); return; }
      if (!uploadPeriodId) { setError('Elige el periodo donde se subirán los PDFs.'); return; }
      await bulkUpload(false);
      return;
    }
    setSaving(true); setError('');
    try {
      if (modal.type === 'section-new') {
        await api.post('/admin/periodicals/sections', form);
        onSuccess(`Sección "${form.name_es}" creada`);
      } else if (modal.type === 'section-edit') {
        await api.put(`/admin/periodicals/sections/${modal.section.id}`, form);
        onSuccess('Sección actualizada');
      } else if (modal.type === 'period-new') {
        await api.post('/admin/periodicals/periods', { ...form, section_id: modal.section.id });
        onSuccess(`Periodo "${form.label_es}" creado`);
      } else if (modal.type === 'period-edit') {
        await api.put(`/admin/periodicals/periods/${modal.period.id}`, { ...form, section_id: modal.section.id });
        onSuccess('Periodo actualizado');
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || 'Error al guardar';
      setError(msg);
      onError && onError(msg);
    } finally {
      setSaving(false);
    }
  }

  const title = {
    'section-new': 'Nueva sección',
    'section-edit': 'Editar sección',
    'period-new': `Nuevo periodo en "${modal.section?.name?.es || ''}"`,
    'period-edit': 'Editar periodo',
    'file-new': `Subir PDF a "${modal.period?.label?.es || ''}"`,
    'section-file-new': `Subir PDFs a "${modal.section?.name?.es || ''}"`,
  }[modal.type] || '';

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-periodicals-modal">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${isFileUpload ? 'max-w-lg' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-charcoal-800">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {(modal.type === 'section-new' || modal.type === 'section-edit') && (
            <>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre (Español) *</label>
                <input value={form.name_es} onChange={(e) => setForm({ ...form, name_es: e.target.value })} required
                  data-testid="modal-section-name-es"
                  placeholder="Ej: Flash 2026"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre (English) <span className="text-charcoal-400">(opcional)</span></label>
                <input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="Ej: Flash 2026"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Pestaña</label>
                <div className="flex flex-wrap gap-2">
                  {(groups.length ? groups : [{ key: 'flash', name_es: 'Flash' }, { key: 'libros', name_es: 'Libros' }]).map((g) => (
                    <button
                      key={g.key} type="button" onClick={() => setForm({ ...form, group: g.key })}
                      data-testid={`modal-section-group-${g.key}`}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${form.group === g.key ? 'bg-cmic-600 text-white border-cmic-600' : 'bg-white text-charcoal-600 border-gray-200 hover:border-cmic-300'}`}
                    >
                      {g.name_es}
                      {g.hidden && <EyeOff size={12} className={form.group === g.key ? 'text-white/80' : 'text-amber-500'} />}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-charcoal-400 mt-1">Define en qué pestaña pública aparece esta sección.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Orden</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                  className="w-32 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
                <p className="text-xs text-charcoal-400 mt-1">Menor = aparece primero. (Tip: usa el botón "Reordenar secciones" para arrastrar visualmente.)</p>
              </div>
            </>
          )}
          {(modal.type === 'period-new' || modal.type === 'period-edit') && (
            <>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Etiqueta (Español) *</label>
                <input value={form.label_es} onChange={(e) => setForm({ ...form, label_es: e.target.value })} required
                  data-testid="modal-period-label-es"
                  placeholder="Ej: Marzo 2026, Diciembre 2025…"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Etiqueta (English) <span className="text-charcoal-400">(opcional)</span></label>
                <input value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })}
                  placeholder="Ej: March 2026"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Orden</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                  className="w-32 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              </div>
            </>
          )}
          {isFileUpload && (
            <>
              {modal.type === 'section-file-new' && (
                <div className="bg-cmic-50/60 border border-cmic-100 rounded-lg p-3 space-y-3">
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setFolderMode(false); setFileObjs([]); setStatuses([]); setDoneSummary(null); }}
                      data-testid="modal-mode-files"
                      className={`flex-1 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${!folderMode ? 'bg-cmic-600 text-white border-cmic-600' : 'bg-white text-charcoal-600 border-gray-200 hover:bg-gray-50'}`}>
                      Archivos sueltos
                    </button>
                    <button type="button" onClick={() => { setFolderMode(true); setFileObjs([]); setStatuses([]); setDoneSummary(null); }}
                      data-testid="modal-mode-folder"
                      className={`flex-1 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${folderMode ? 'bg-cmic-600 text-white border-cmic-600' : 'bg-white text-charcoal-600 border-gray-200 hover:bg-gray-50'}`}>
                      Carpeta (auto-organiza)
                    </button>
                  </div>
                  {!folderMode ? (
                    sectionPeriods.length === 0 ? (
                      <p className="text-xs text-amber-700">Esta sección no tiene periodos. Crea uno con "Agregar periodo" o usa el modo "Carpeta".</p>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-charcoal-700 mb-1.5">Periodo destino *</label>
                        <select value={targetPeriodId} onChange={(e) => setTargetPeriodId(e.target.value)}
                          data-testid="modal-section-period-select"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500">
                          {sectionPeriods.map((p) => (
                            <option key={p.id} value={p.id}>{p.label?.es} ({p.files?.length || 0} PDF)</option>
                          ))}
                        </select>
                      </div>
                    )
                  ) : (
                    <div>
                      <p className="text-[11px] text-charcoal-600 mb-2">Selecciona una carpeta: <strong>cada subcarpeta se vuelve un periodo</strong> y sus PDFs se suben dentro automáticamente. Si una subcarpeta/periodo ya existe, los archivos se añaden ahí.</p>
                      <button type="button" onClick={() => inputFolderRef.current?.click()} data-testid="modal-folder-pick"
                        className="inline-flex items-center gap-2 bg-white border border-cmic-300 text-cmic-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-50">
                        <FolderPlus size={15} /> Elegir carpeta
                      </button>
                      <input ref={inputFolderRef} type="file" webkitdirectory="" directory="" multiple
                        onChange={(e) => {
                          const all = Array.from(e.target.files || []).filter((f) => /\.pdf$/i.test(f.name));
                          setFileObjs(all); setStatuses([]); setDoneSummary(null);
                        }}
                        data-testid="modal-folder-input" className="hidden" />
                      {Object.keys(folderGroups).length > 0 && (
                        <div className="mt-3 border border-cmic-100 rounded-lg divide-y divide-cmic-50 bg-white max-h-40 overflow-y-auto" data-testid="modal-folder-mapping">
                          {Object.keys(folderGroups).sort().map((label) => (
                            <div key={label} className="flex items-center justify-between px-3 py-1.5 text-xs">
                              <span className="font-medium text-charcoal-700 truncate flex items-center gap-1.5">
                                <FolderPlus size={12} className="text-cmic-500" /> {label}
                              </span>
                              <span className="text-charcoal-500 flex-shrink-0">{folderGroups[label].length} PDF → periodo</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {!folderMode && (
                <div>
                  <label className="block text-xs font-medium text-charcoal-600 mb-1.5">
                    Título <span className="text-charcoal-400">(opcional — si subes varios, se usa el nombre de cada archivo)</span>
                  </label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="modal-file-title" disabled={fileObjs.length > 1}
                    placeholder="Ej: Flash Mayo 2026"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 disabled:bg-gray-50 disabled:text-charcoal-400" />
                </div>
              )}
              <div>
                {!folderMode && (
                  <>
                    <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Archivos PDF * (puedes seleccionar o arrastrar varios)</label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const dropped = Array.from(e.dataTransfer.files || []).filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
                        if (dropped.length) setFileObjs((prev) => [...prev, ...dropped]);
                      }}
                      onClick={() => inputFileRef.current?.click()}
                      data-testid="modal-file-dropzone"
                      className="border-2 border-dashed border-gray-300 rounded-xl px-4 py-6 text-center cursor-pointer hover:border-cmic-400 hover:bg-cmic-50/40 transition-colors"
                    >
                      <Upload size={22} className="mx-auto text-charcoal-400 mb-2" />
                      <p className="text-sm text-charcoal-600 font-medium">Arrastra tus PDFs aquí o haz clic para seleccionar</p>
                      <p className="text-xs text-charcoal-400 mt-1">Puedes subir muchos a la vez (máx 25 MB c/u)</p>
                    </div>
                    <input ref={inputFileRef} type="file" accept="application/pdf" multiple
                      onChange={(e) => setFileObjs(Array.from(e.target.files || []))}
                      data-testid="modal-file-input" className="hidden" />
                  </>
                )}
                {fileObjs.length > 0 && (
                  <div className="mt-3" data-testid="modal-file-list">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-charcoal-500">{fileObjs.length} PDF(s) en cola</span>
                      {!saving && !doneSummary && (
                        <button type="button" onClick={() => { setFileObjs([]); setStatuses([]); }} className="text-xs text-charcoal-400 hover:text-red-500">Quitar todos</button>
                      )}
                    </div>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {fileObjs.map((f, i) => {
                        const st = statuses[i]?.state;
                        return (
                          <div key={i} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                            <span className="text-xs text-charcoal-600 truncate flex items-center gap-1.5 min-w-0">
                              {st === 'uploading' ? <Loader2 size={12} className="animate-spin text-cmic-500 flex-shrink-0" />
                                : st === 'done' ? <CheckCircle2 size={12} className={`flex-shrink-0 ${statuses[i]?.warned ? 'text-amber-500' : 'text-green-600'}`} />
                                : st === 'error' ? <AlertCircle size={12} className="text-red-500 flex-shrink-0" />
                                : <FileText size={12} className="text-red-500 flex-shrink-0" />}
                              <span className="truncate">{f.name} · {(f.size / (1024 * 1024)).toFixed(1)} MB</span>
                            </span>
                            {st === 'error' ? (
                              <span className="text-[10px] text-red-500 flex-shrink-0 truncate max-w-[40%]">{statuses[i]?.msg}</span>
                            ) : st === 'done' && statuses[i]?.warned ? (
                              <span className="text-[10px] text-amber-600 flex-shrink-0">sin texto</span>
                            ) : (!saving && !doneSummary) ? (
                              <button type="button" onClick={() => setFileObjs((prev) => prev.filter((_, idx) => idx !== i))} className="text-charcoal-400 hover:text-red-500 flex-shrink-0"><X size={13} /></button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {doneSummary && (
                  <div className="mt-3 text-xs rounded-lg p-3 bg-green-50 border border-green-200 text-green-800" data-testid="modal-file-summary">
                    ✓ {doneSummary.ok} subido(s){doneSummary.periods ? ` en ${doneSummary.periods} periodo(s)` : ''}{doneSummary.warned ? ` · ${doneSummary.warned} sin texto buscable` : ''}{doneSummary.failed ? ` · ${doneSummary.failed} con error` : ''}.
                  </div>
                )}
              </div>
              <p className="text-xs text-charcoal-400 bg-cmic-50 border border-cmic-100 rounded-lg p-3">
                Al subir, el texto de cada PDF se extrae automáticamente y queda buscable desde la página pública.
              </p>
            </>
          )}
          {error && (<div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{error}</div>)}
          <div className="flex justify-end gap-2 pt-2">
            {isFileUpload && doneSummary ? (
              <>
                {doneSummary.failed > 0 && !folderMode && (
                  <button type="button" onClick={() => bulkUpload(true)} disabled={saving}
                    data-testid="modal-retry-failed-btn"
                    className="inline-flex items-center gap-2 bg-white border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-50 disabled:opacity-60">
                    <RefreshCw size={14} /> Reintentar fallidos
                  </button>
                )}
                <button type="button" onClick={onClose}
                  data-testid="modal-close-btn"
                  className="inline-flex items-center gap-2 bg-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700">
                  Cerrar
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
                <button type="submit" disabled={saving || (isFileUpload && (fileObjs.length === 0 || (!folderMode && modal.type === 'section-file-new' && !uploadPeriodId)))}
                  data-testid="modal-submit-btn"
                  className="inline-flex items-center gap-2 bg-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700 disabled:opacity-60">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : (isFileUpload ? <Upload size={14} /> : <Save size={14} />)}
                  {isFileUpload ? (folderMode ? `Crear periodos y subir ${fileObjs.length || ''}` : `Subir ${fileObjs.length || ''} PDF(s)`) : 'Guardar'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function initForm(modal) {
  if (modal.type === 'section-new') return { name_es: '', name_en: '', sort_order: 0, group: modal.group || 'flash' };
  if (modal.type === 'section-edit') return {
    name_es: modal.section?.name?.es || '',
    name_en: modal.section?.name?.en || '',
    sort_order: 0,
    group: modal.section?.group || 'flash',
  };
  if (modal.type === 'period-new') return { label_es: '', label_en: '', sort_order: 0 };
  if (modal.type === 'period-edit') return {
    label_es: modal.period?.label?.es || '',
    label_en: modal.period?.label?.en || '',
    sort_order: 0,
  };
  if (modal.type === 'file-new' || modal.type === 'section-file-new') return { title: '' };
  return {};
}


// Diagnostics modal: shows for each periodical PDF whether the search index
// has extracted text. PDFs that are scanned images (no text layer) will show
// "Sin texto extraíble" — those need OCR before they can be searched.
function DiagnosticsModal({ onClose }) {
  const [state, setState] = useState({ loading: true, files: [], error: '' });
  const [reindexing, setReindexing] = useState(false);

  async function load() {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const { data } = await api.get('/admin/periodicals/diagnostics');
      setState({ loading: false, files: data.files || [], error: '' });
    } catch (e) {
      setState({ loading: false, files: [], error: formatApiError(e) });
    }
  }

  async function reindex() {
    setReindexing(true);
    try {
      await api.post('/admin/periodicals/reindex');
      await load();
    } finally {
      setReindexing(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const searchable = state.files.filter((f) => f.searchable).length;
  const total = state.files.length;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-periodicals-diagnostics-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-semibold text-charcoal-800 flex items-center gap-2">
              <Stethoscope size={18} className="text-cmic-600" />
              Diagnóstico de búsqueda
            </h2>
            <p className="text-xs text-charcoal-500 mt-0.5">
              {state.loading ? 'Cargando…' : `${searchable} de ${total} PDFs son buscables`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reindex} disabled={reindexing} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-charcoal-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50">
              {reindexing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Reindexar
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={18} /></button>
          </div>
        </div>
        <div className="px-6 py-4">
          {state.error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 mb-4">{state.error}</div>
          )}
          {!state.loading && state.files.length === 0 && (
            <p className="text-sm text-charcoal-500 py-8 text-center">No hay archivos cargados.</p>
          )}
          <div className="space-y-2">
            {state.files.map((f) => {
              const ok = f.searchable && !f.warning;
              return (
                <div key={f.file_id} className={`rounded-lg border p-3 ${ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-charcoal-800 truncate">{f.title}</p>
                      <p className="text-[11px] text-charcoal-500 mt-0.5 truncate">{f.filename}</p>
                    </div>
                    <div className="text-right text-xs flex-shrink-0">
                      {ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 size={12} /> Buscable</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-medium"><AlertCircle size={12} /> Atención</span>
                      )}
                      <p className="text-charcoal-400 mt-1">{f.pages_indexed} pág. · {(f.text_chars / 1000).toFixed(1)}k chars</p>
                    </div>
                  </div>
                  {f.warning && (
                    <p className="mt-2 text-xs text-amber-800">{f.warning}</p>
                  )}
                  {f.sample_text && ok && (
                    <p className="mt-2 text-[11px] text-charcoal-500 italic line-clamp-1">"{f.sample_text}"</p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-5 p-3 bg-cmic-50 border border-cmic-100 rounded-lg text-xs text-charcoal-700 leading-relaxed">
            <strong className="text-cmic-700">Si un PDF dice "Sin texto extraíble":</strong> es un PDF escaneado (imagen). La búsqueda no puede leerlo. Soluciones:
            <ol className="list-decimal ml-5 mt-1.5 space-y-0.5">
              <li>Abre el PDF en Adobe Acrobat → <em>Herramientas → Reconocer texto → En este archivo</em>.</li>
              <li>O usa el sitio gratuito <em>ilovepdf.com/ocr-pdf</em>.</li>
              <li>Vuelve a subir el PDF resultante (reemplaza el anterior).</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}


// Manage the public tabs (groups). Create, rename (ES/EN), show/hide and
// reorder. Hidden tabs disappear from the public page but keep their sections
// and files intact. A tab with sections cannot be deleted until it is emptied.
function GroupsModal({ groups, onClose, onChanged, showOk, showErr }) {
  const [rows, setRows] = useState(groups || []);
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState({ es: '', en: '' });
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState({ es: '', en: '' });
  const dnd = useReorder(rows, (g) => g.id);

  useEffect(() => { setRows(groups || []); }, [groups]);

  const visible = useMemo(() => {
    if (!dnd.reorderMode) return rows;
    const byId = Object.fromEntries(rows.map((g) => [g.id, g]));
    return dnd.orderDraft.map((id) => byId[id]).filter(Boolean);
  }, [rows, dnd.reorderMode, dnd.orderDraft]);

  async function reload() {
    const { data } = await api.get('/admin/periodicals/groups');
    setRows(data || []);
    onChanged && onChanged();
  }

  async function createGroup() {
    if (!newName.es.trim()) { showErr('El nombre de la pestaña es requerido'); return; }
    setCreating(true);
    try {
      await api.post('/admin/periodicals/groups', { name_es: newName.es.trim(), name_en: newName.en.trim(), hidden: false });
      showOk(`Pestaña "${newName.es.trim()}" creada`);
      setNewName({ es: '', en: '' });
      await reload();
    } catch (e) {
      showErr(formatApiError(e.response?.data?.detail) || 'No se pudo crear la pestaña');
    } finally { setCreating(false); }
  }

  async function toggleHidden(g) {
    setBusyId(g.id);
    try {
      await api.put(`/admin/periodicals/groups/${g.id}`, { name_es: g.name_es, name_en: g.name_en, hidden: !g.hidden });
      showOk(g.hidden ? `"${g.name_es}" ahora es visible` : `"${g.name_es}" ahora está oculta`);
      await reload();
    } catch (e) {
      showErr(formatApiError(e.response?.data?.detail) || 'No se pudo actualizar');
    } finally { setBusyId(null); }
  }

  async function saveEdit(g) {
    if (!editName.es.trim()) { showErr('El nombre es requerido'); return; }
    setBusyId(g.id);
    try {
      await api.put(`/admin/periodicals/groups/${g.id}`, { name_es: editName.es.trim(), name_en: editName.en.trim(), hidden: g.hidden });
      showOk('Pestaña actualizada');
      setEditId(null);
      await reload();
    } catch (e) {
      showErr(formatApiError(e.response?.data?.detail) || 'No se pudo actualizar');
    } finally { setBusyId(null); }
  }

  async function deleteGroup(g) {
    if ((g.section_count || 0) > 0) {
      showErr(`"${g.name_es}" tiene ${g.section_count} sección(es). Muévelas o bórralas antes de eliminar la pestaña.`);
      return;
    }
    if (!window.confirm(`¿Eliminar la pestaña "${g.name_es}"?`)) return;
    setBusyId(g.id);
    try {
      await api.delete(`/admin/periodicals/groups/${g.id}`);
      showOk(`Pestaña "${g.name_es}" eliminada`);
      await reload();
    } catch (e) {
      showErr(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar');
    } finally { setBusyId(null); }
  }

  async function saveOrder() {
    try {
      await dnd.saveOrder(async (ids) => {
        await api.put('/admin/periodicals/groups/reorder', { ids });
        showOk('Orden de pestañas actualizado');
        await reload();
      });
    } catch (e) {
      showErr('No se pudo guardar el orden');
    }
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-periodicals-groups-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-semibold text-charcoal-800 flex items-center gap-2"><Layers size={18} className="text-cmic-600" /> Pestañas públicas</h2>
            <p className="text-xs text-charcoal-500 mt-0.5">Crea, renombra, oculta o reordena las pestañas de Publicaciones.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={18} /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Create */}
          <div className="bg-cmic-50/60 border border-cmic-100 rounded-xl p-3">
            <label className="block text-xs font-medium text-charcoal-700 mb-1.5">Nueva pestaña</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={newName.es} onChange={(e) => setNewName({ ...newName, es: e.target.value })}
                data-testid="new-group-name-es" placeholder="Nombre (Español) *"
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              <input value={newName.en} onChange={(e) => setNewName({ ...newName, en: e.target.value })}
                data-testid="new-group-name-en" placeholder="Name (English)"
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              <button onClick={createGroup} disabled={creating}
                data-testid="create-group-btn"
                className="inline-flex items-center justify-center gap-1.5 bg-cmic-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700 disabled:opacity-60 flex-shrink-0">
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Crear
              </button>
            </div>
          </div>

          {/* Reorder controls */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-charcoal-500">{rows.length} pestaña(s)</span>
            {!dnd.reorderMode ? (
              <button onClick={dnd.enterReorder} disabled={rows.length < 2}
                data-testid="reorder-groups-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-600 hover:text-cmic-600 disabled:opacity-40">
                <ArrowUpDown size={12} /> Reordenar
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-600 hover:text-charcoal-800"><X size={12} /> Cancelar</button>
                <button onClick={saveOrder} disabled={dnd.saving}
                  data-testid="save-groups-order-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-60">
                  {dnd.saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar orden
                </button>
              </div>
            )}
          </div>

          {/* List */}
          <div className="space-y-2">
            {visible.map((g) => (
              <div key={g.id}
                data-testid={`group-row-${g.key}`}
                draggable={dnd.reorderMode}
                {...(dnd.reorderMode ? dnd.dragProps(g.id) : {})}
                className={`rounded-xl border p-3 ${dnd.reorderMode ? 'border-amber-300 bg-amber-50/40 cursor-move' : g.hidden ? 'border-gray-200 bg-gray-50/60' : 'border-gray-200 bg-white'}`}>
                {editId === g.id ? (
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                    <input value={editName.es} onChange={(e) => setEditName({ ...editName, es: e.target.value })}
                      data-testid={`edit-group-name-es-${g.key}`} placeholder="Español"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
                    <input value={editName.en} onChange={(e) => setEditName({ ...editName, en: e.target.value })}
                      data-testid={`edit-group-name-en-${g.key}`} placeholder="English"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
                    <div className="flex gap-1.5">
                      <button onClick={() => saveEdit(g)} disabled={busyId === g.id}
                        data-testid={`save-group-edit-${g.key}`}
                        className="inline-flex items-center gap-1 bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-emerald-600 disabled:opacity-60">
                        {busyId === g.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      </button>
                      <button onClick={() => setEditId(null)} className="px-3 py-1.5 rounded-lg text-xs text-charcoal-500 hover:bg-gray-100"><X size={12} /></button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {dnd.reorderMode && <GripVertical size={14} className="text-amber-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-charcoal-800 truncate flex items-center gap-1.5">
                          {g.name_es}
                          {g.hidden && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full"><EyeOff size={10} /> Oculta</span>}
                        </p>
                        <p className="text-[11px] text-charcoal-400 truncate">{g.name_en} · {g.section_count || 0} sección(es)</p>
                      </div>
                    </div>
                    {!dnd.reorderMode && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => toggleHidden(g)} disabled={busyId === g.id}
                          data-testid={`toggle-group-visibility-${g.key}`}
                          title={g.hidden ? 'Mostrar pestaña' : 'Ocultar pestaña'}
                          className="p-1.5 rounded-lg text-charcoal-400 hover:bg-gray-100 hover:text-charcoal-700 disabled:opacity-50">
                          {busyId === g.id ? <Loader2 size={14} className="animate-spin" /> : g.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button onClick={() => { setEditId(g.id); setEditName({ es: g.name_es, en: g.name_en }); }}
                          data-testid={`edit-group-${g.key}`}
                          title="Renombrar"
                          className="p-1.5 rounded-lg text-charcoal-400 hover:bg-gray-100 hover:text-charcoal-700"><Pencil size={14} /></button>
                        <button onClick={() => deleteGroup(g)} disabled={busyId === g.id}
                          data-testid={`delete-group-${g.key}`}
                          title={(g.section_count || 0) > 0 ? 'Vacía la pestaña antes de eliminarla' : 'Eliminar pestaña'}
                          className="p-1.5 rounded-lg text-charcoal-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
